import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import ApprovalApplication from './components/auth/ApprovalApplication';
import PendingApproval from './components/auth/PendingApproval';
import OrganisationRequestForm from './components/auth/OrganisationRequestForm';
import AcceptInvite from './components/auth/AcceptInvite';
import Onboarding from './components/onboarding/Onboarding';
import Sites from './components/dashboard/Sites';
import Dashboard from './components/dashboard/Dashboard';
import POSCallback from './components/dashboard/POSCallback';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Single source of truth for light/dark theme tokens
const getDesignTokens = (mode) => ({
  palette: {
    mode,
    primary: {
      main: '#ff6b2c',
      light: '#ffe2d2',
      dark: '#c44108',
    },
    secondary: {
      main: '#1f2933',
    },
    background:
      mode === 'dark'
        ? {
            default: '#0b1120',
            paper: '#020617',
          }
        : {
            default: '#f6f6f8',
            paper: '#ffffff',
          },
    text:
      mode === 'dark'
        ? {
            primary: '#f9fafb',
            secondary: '#9ca3af',
          }
        : {
            primary: '#0b1120',
            secondary: '#4b5563',
          },
    error: {
      main: '#f97373',
    },
  },
  typography: {
    fontFamily: '"Inter", "Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightBold: 700,
  },
  shape: {
    borderRadius: 16,
  },
});

const RequireApprovedUser = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.approvalStatus === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  return children;
};

function App() {
  // Initialise from system/browser colour scheme
  const [mode, setMode] = React.useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  // Keep in sync with system changes; later you can override this from a dashboard toggle
  React.useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event) => {
      setMode(event.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, []);

  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Treat the approval application as the public sign-up flow */}
            <Route path="/signup" element={<ApprovalApplication />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {/* Organisation form – later this will be opened from an email link */}
            <Route path="/organisation" element={<OrganisationRequestForm />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/apply" element={<ApprovalApplication />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route
              path="/onboarding"
              element={
                <RequireApprovedUser>
                  <Onboarding />
                </RequireApprovedUser>
              }
            />
            {/* Public POS OAuth callback route (used by Square/Lightspeed/Zettle) */}
            <Route path="/dashboard/pos/callback" element={<POSCallback />} />
            <Route
              path="/sites"
              element={
                <RequireApprovedUser>
                  <Sites />
                </RequireApprovedUser>
              }
            />
            <Route
              path="/dashboard/*"
              element={
                <RequireApprovedUser>
                  <Dashboard />
                </RequireApprovedUser>
              }
            />
            <Route path="/" element={<Navigate to="/login" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;